import type { RecordSourceProxy, RecordSourceSelectorProxy } from "relay-runtime";

import type { KeyResultAttributes } from "~/okrs/KeyResult";
import type { ObjectiveAttributes } from "~/okrs/Objective";

export const calculateObjectiveProgress = (
	keyResults: KeyResultAttributes[],
	children?: ObjectiveAttributes[],
): number => {
	let weightSum = 0;
	let progressSum = 0;

	keyResults.forEach(({ progress, weight }) => {
		weightSum += weight;
		progressSum += progress * weight;
	});

	if (children) {
		children.forEach(({ progress, weight }) => {
			weightSum += weight;
			progressSum += progress * weight;
		});
	}

	return weightSum !== 0 ? Math.floor(progressSum / weightSum) : 0;
};

export const calculateKeyResultProgress = (keyResult: KeyResultAttributes): number => {
	const adjustedCurrent = keyResult.metricCurrent - keyResult.metricStart;
	const adjustedTarget = keyResult.metricTarget - keyResult.metricStart;
	if (adjustedCurrent === 0) {
		return adjustedTarget === 0 ? 100 : 0;
	}
	const calculatedProgress = Math.floor((adjustedCurrent / adjustedTarget) * 100);
	return Math.min(100, Math.max(calculatedProgress, 0));
};

/*
	OKRs contains some scenarios that Relay (as of September 2021) does not support yet.
	Let's take a look at an example of one of those scenarios and why
	Relay doesn't properly support that.

	Let's say that we have a query which returns a list of items.
	That query also accepts some variables that act as filters for that list.
	For the purpose of this example let's say that the query accepts the variable status as a filter
	If we call that query for all status we get the items A, B and C.
	If we call that same query but now for the "completed" status we get the item B.
	If we now go and edit item C to have the status "completed", the first query
	will still show the items A, B and C. But the second query will still only show the item B.
	Unless we manually do a refetch for that query with those variables in specific.

	You would assume that you could solve that issue by using `fetchKey` with the
	"store-or-network" `fetchPolicy` but that is not the case. By the `useLazyLoadQuery` definition
	of the parameter `fetchKey` we have:
		If the fetchKey is different from the one used in the previous render,
		the current query will be re-evaluated against the store,
		and it might be refetched depending on the current fetchPolicy and the state of the cache.
	That is a little generic and doesn't tell us much, but I would like to give a special attention
	for the part where it says "and it might be refetched". What that means, after also
	doing some tests and talking with Relay's community on discord, is that by upgrading
	the `fetchKey` while using "store-or-network" that won't necessarily trigger a refetch,
	even though it seems like it might. And because of that when we fire a new request to fetch
	the list for the status being "completed" with an updated `fetchKey` that doesn't guarantee
	that it will trigger a refetch and we end up missing the old data.

	There are a bunch of different approaches that might come to your mind to fix this,
	one of them beign to try to locally update the store. But the problem is that the
	relay store keeps one RelayConnection for each different set of variables for a query,
	and while it's easy to know which connections should have that item removed it is not easy
	to know which connections should have that item added.
	Each set of variables creates a new connection for that query, so you end up having different
	connections for all the filters that the user has tried.
	Another idea might be change the `fetchPolicy` to be "store-and-network" or "network-only".
	That works if you change it to that and never rollback (because again you don't know when)
	you have updated all the needed Connections. While that works we loose the ability to cache
	once a user creates or edits an item and we change the `fetchPolicy`.

	There might be more solutions that may come to your mind, I know I have tried a bunch of them
	but the problem always boild down to the fact that we simple don't know
	which connections we should update. And sadly relay's doesn't offer us an API where we can say
	that all records related to one specific query is not valid anymore and so need to be fetched
	from the network the next time. Relay offers a way for you to mark a record as "stale" and
	doing so will force all queries that contains that record to fetch from the "network" the next
	time. But only marking the edited item's record as "stale" is not enough since there are queries
	that might need a refetch and them didn't had that record in them (like in our example).

	After a lot of discussions and brainstorming the best solution that we found was to, in a way,
	create our own way of invalidating all data related to a query.
	To do that we need to get all records that is of the type of our connection and mark
	the whole record as stale, basically we invalidate all stored data for that query.
	This is what this function does, we make use of some relay's internal values
	to iterate through the store, find the ones that are the type of our connection
	and mark them as "stale".

	TODO: We can be a little more clever here and only mark the ones that had
	one of the variables changed by our edit operation or used by the create operation
	to be marked as stale. So we still keep the store for the ones that we know for a fact
	that haven't changed.
*/

//

export const staleObjectiveRecords = (store: RecordSourceSelectorProxy | RecordSourceProxy) => {
	const viewer = store.get("viewer");
	if (!viewer) {
		return;
	}
	// Relay uses this pattern where the information flows from a sink to the base variable.
	// I am not entirely sure how it works and its purposes but I know that it's related to
	// the fact that they don't dispatch updates immediately and the current
	// state of the store might be on the sink if you end up in a edge case situation.
	// @ts-ignore
	// eslint-disable-next-line no-underscore-dangle
	let source = viewer._mutator._sink.get(viewer.getDataID());
	if (!source) {
		// @ts-ignore
		// eslint-disable-next-line no-underscore-dangle
		source = viewer._mutator._base.get(viewer.getDataID());
		if (!source) {
			return;
		}
	}
	for (const key in source) {
		try {
			if (viewer.getLinkedRecord(key)?.getType() === "ObjectivesConnection") {
				viewer.getLinkedRecord(key)?.invalidateRecord();
			}
		} catch (e) {}
	}
};
